import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Mail, Plus, Undo2, CheckCircle2 } from "lucide-react";
import { api, API, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { money, todayStr } from "../lib/helpers";
import { PageHeader, Empty, Field, NativeSelect } from "../components/Common";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { InvoiceDialog } from "../components/InvoiceDialog";

const STATUS = { paid: "bg-emerald-500/15 text-emerald-800", unpaid: "bg-amber-500/15 text-amber-800", credited: "bg-muted text-muted-foreground",
  partially_credited: "bg-sky-500/15 text-sky-800", issued: "bg-rose-500/15 text-rose-800" };
const monthStart = () => `${todayStr().slice(0, 8)}01`;

function Row({ inv, can, reload }) {
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const creditNote = () => {
    const left = (inv.total - (inv.credited_total || 0)).toFixed(2);
    const v = window.prompt(`Credit note for ${inv.number}. Amount to refund (max ${left}):`, left);
    if (v === null) return;
    const reason = window.prompt("Reason (shown on the credit note):", "Refund") ?? "";
    act(() => api.post(`/invoices/${inv.id}/credit-note`, { amount: Number(v), reason }), "Credit note created");
  };
  const email = () => {
    const to = window.prompt("Send to:", inv.buyer?.email || "");
    if (to) act(() => api.post(`/invoices/${inv.id}/email`, { to }), "Invoice emailed");
  };
  const paid = () => { const m = window.prompt("Payment method (card, cash, bank_transfer, sumup, other):", "card"); if (m) act(() => api.post(`/invoices/${inv.id}/mark-paid`, { method: m }), "Marked as paid"); };
  const credited = inv.kind === "invoice" && inv.total - (inv.credited_total || 0) > 0.009;
  return (
    <tr data-testid={`invoice-row-${inv.number}`}>
      <td className="px-4 py-3 font-mono text-xs font-semibold">{inv.number}{inv.credits_number && <div className="font-normal text-muted-foreground">for {inv.credits_number}</div>}</td>
      <td className="px-4 py-3 font-mono text-xs">{inv.date}</td>
      <td className="px-4 py-3">{inv.buyer?.name || "—"}<div className="text-xs text-muted-foreground">{inv.lines?.[0]?.description}{inv.lines?.length > 1 ? ` +${inv.lines.length - 1}` : ""}</div></td>
      <td className="px-4 py-3">{inv.location_name}</td>
      <td className="px-4 py-3 text-right">{money(inv.tax_total, inv.currency)}</td>
      <td className="px-4 py-3 text-right font-semibold">{money(inv.total, inv.currency)}</td>
      <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs ${STATUS[inv.status] || ""}`} data-testid={`invoice-status-${inv.number}`}>{inv.kind === "credit_note" ? "credit note" : inv.status.replace("_", " ")}</span></td>
      <td className="px-4 py-3 whitespace-nowrap">
        <a href={`${API}/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" title="PDF" data-testid={`invoice-pdf-${inv.number}`} className="inline-flex p-1.5 hover:text-primary"><FileText className="h-4 w-4" /></a>
        {can("payments.write") && <>
          <button title="Email" data-testid={`invoice-email-${inv.number}`} onClick={email} className="p-1.5 hover:text-primary"><Mail className="h-4 w-4" /></button>
          {inv.status === "unpaid" && <button title="Mark as paid" data-testid={`invoice-paid-${inv.number}`} onClick={paid} className="p-1.5 hover:text-primary"><CheckCircle2 className="h-4 w-4" /></button>}
          {credited && inv.status !== "unpaid" && <button title="Credit note / refund" data-testid={`invoice-credit-${inv.number}`} onClick={creditNote} className="p-1.5 hover:text-destructive"><Undo2 className="h-4 w-4" /></button>}
        </>}
      </td>
    </tr>
  );
}

export default function Invoices() {
  const { locationId, can } = useApp();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const params = { date_from: from, date_to: to, location_id: locationId || undefined };
  const load = () => api.get("/invoices", { params: { ...params, q: q || undefined, kind: kind || undefined } }).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [from, to, locationId, q, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const exportUrl = `${API}/invoices/export.csv?${new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString()}`;
  return (
    <div>
      <PageHeader eyebrow="Accounting" title="Invoices">
        <Field label="From"><Input type="date" data-testid="invoices-from" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><Input type="date" data-testid="invoices-to" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <a href={exportUrl} className="self-end" data-testid="invoices-export-btn"><Button variant="outline" className="rounded-full"><Download className="h-4 w-4 mr-1.5" />Export for accountant</Button></a>
        {can("payments.write") && <Button className="rounded-full self-end" data-testid="invoices-new-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1.5" />New invoice</Button>}
      </PageHeader>
      <div className="flex flex-wrap gap-3 mb-6">
        <Input className="max-w-xs" placeholder="Search number, name or email" data-testid="invoices-search" value={q} onChange={(e) => setQ(e.target.value)} />
        <NativeSelect testId="invoices-kind" value={kind} onChange={setKind} options={[{ value: "", label: "Invoices & credit notes" }, { value: "invoice", label: "Invoices" }, { value: "credit_note", label: "Credit notes" }]} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6" data-testid="invoices-totals">
        {data && Object.entries(data.totals).map(([cur, v]) => (
          <div key={cur} className="rounded-[var(--radius)] border bg-card p-5" data-testid={`invoices-total-${cur}`}>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{cur} · {v.count} documents</p>
            <p className="font-display text-2xl font-bold mt-1">{money(v.total, cur)}</p>
            <p className="text-xs text-muted-foreground mt-1">Net {money(v.net, cur)} · VAT {money(v.tax, cur)}</p>
          </div>
        ))}
      </div>
      <div className="rounded-[var(--radius)] border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
            <tr>{["Number", "Date", "Customer", "Location", "VAT", "Total", "Status", ""].map((h) => <th key={h} className={`font-medium px-4 py-3 ${["VAT", "Total"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y">{data?.items.map((inv) => <Row key={inv.id} inv={inv} can={can} reload={load} />)}</tbody>
        </table>
        {data && !data.items.length && <div className="p-6"><Empty text="No invoices in this period. Invoices are created automatically when payments are recorded." /></div>}
      </div>
      <InvoiceDialog open={open} onOpenChange={setOpen} onDone={load} />
    </div>
  );
}
